
import { Options, prop, Vue } from "vue-class-component";
import {
  AdditionalDocumentBookingV4,
  DocInternationalBookingV4,
  DutyTaxTermsBookingV4,
  ErrorBookingV4,
  SupportingDocBookingV4
} from "@/domain/entities/BookingV4";
import UploadImageTiny from "../upload-image-tiny/index.vue";
import { SttAdjustmentRule } from "@/domain/entities/ShipmentBooking";
import { ConfigValidationForm } from "@/domain/entities/CreateBookingV3";
import { UploadMultiple } from "@/domain/entities/MainApp";
import { BookingController } from "@/app/ui/controllers/BookingController";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { UploadHydraFileRequest } from "@/data/payload/api/BookingRequest";
import {
  ADDITIONAL_DOCUMENT_MAX_COUNT,
  additionalDocumentFileSchema,
  dutyTaxTermsSchema,
  emailSchema,
  idNumberSchema,
  imageRequiredError,
  parseValidationError,
  taxNumberSchema
} from "./validation";
import { formatInputNumberOnlyV2 } from "@/app/infrastructures/misc/Utils";
import { formatNumberOnlyWithZero } from "@/app/infrastructures/misc/common-library/FormatNumberOnlyWithZero";

export type SupportingDocFieldKey =
  | "email"
  | "idNumber"
  | "taxNumber"
  | "imageId"
  | "imageTax"
  | "imageBeforePacking"
  | "imageAfterPacking"
  | "dutyTaxTerms";

type SupportingDocImageFieldKey =
  | "imageId"
  | "imageTax"
  | "imageBeforePacking"
  | "imageAfterPacking";

class Props {
  modelValue = prop<SupportingDocBookingV4>({
    required: true,
    default: () => new SupportingDocBookingV4()
  });
  isFtz = prop<boolean>({
    required: true,
    type: Boolean,
    default: false
  });
  isCipl = prop<boolean>({
    required: true,
    type: Boolean,
    default: false
  });
  fieldRules = prop<
    Partial<Record<SupportingDocFieldKey, SttAdjustmentRule>>
  >({
    required: true,
    default: () => ({})
  });
  // Adjust v4 per-field rules (GENESIS-20184) — inter*/ftz* config rules.
  // fieldRules above stays the isRequired source; this one drives disable/hide.
  configRules = prop<
    Partial<Record<SupportingDocFieldKey, SttAdjustmentRule>>
  >({
    required: true,
    default: () => ({})
  });
  showRequiredImageErrors = prop<boolean>({
    default: false,
    type: Boolean
  });
  errors = prop<ErrorBookingV4>({
    required: true,
    default: () => new ErrorBookingV4(),
    type: Object
  });
  isRetail = prop<boolean>({ default: false, type: Boolean });
  isEdit = prop<boolean>({ default: false, type: Boolean });
  isReverseJourney = prop<boolean>({ default: false, type: Boolean });
}

@Options({
  name: "SupportingDocSectionV4",
  emits: ["update:modelValue", "validationChange"],
  watch: {
    modelValue: {
      deep: true,
      immediate: true,
      handler() {
        this.emitValidationChange();
      }
    },
    fieldRules: {
      deep: true,
      immediate: true,
      handler() {
        if (this.showRequiredImageErrors) {
          this.touchAllFields();
          this.validateRequiredImages();
        }
        this.emitValidationChange();
      }
    },
    configRules: {
      deep: true,
      immediate: true,
      handler() {
        this.emitValidationChange();
      }
    },
    showRequiredImageErrors(value: boolean) {
      if (!value) return;
      this.touchAllFields();
      this.validateRequiredImages();
    },
    isFtz() {
      // GENESIS-23380 — Fixing ini mungkin impact ke Booking Client
      // International dan Domestic FTZ. Minta tolong cek upload >5 MB antar-tab.
      this.resetUploadErrors();
      if (this.showRequiredImageErrors) this.validateRequiredImages();
      this.emitValidationChange();
    },
    isCipl() {
      this.emitValidationChange();
    }
  },
  components: {
    UploadImageTiny
  }
})
export default class SupportingDocSectionV4 extends Vue.with(Props) {
  readonly maxAdditionalDocuments = ADDITIONAL_DOCUMENT_MAX_COUNT;

  isDutyTaxTermsMenuOpen = false;
  guidePanelCollapsed = false;
  additionalDocumentError = "";
  touched: Record<
    "email" | "idNumber" | "taxNumber" | "dutyTaxTerms",
    boolean
  > = {
    email: false,
    idNumber: false,
    taxNumber: false,
    dutyTaxTerms: false
  };
  imageErrors: Record<
    "imageId" | "imageTax" | "imageBeforePacking" | "imageAfterPacking",
    string
  > = {
    imageId: "",
    imageTax: "",
    imageBeforePacking: "",
    imageAfterPacking: ""
  };

  $refs!: {
    additionalDocumentInput: HTMLInputElement;
  };

  mounted() {
    this.emitValidationChange();
  }

  get activeDoc(): DocInternationalBookingV4 {
    return this.isFtz
      ? this.modelValue.ftzForm
      : this.modelValue.docInternational;
  }

  // Context key memaksa uploader memakai state validasi baru saat tab berganti.
  get documentContextKey(): string {
    return this.isFtz ? "ftz" : "international";
  }

  // Bersihkan pesan error lokal agar tidak pindah ke konteks dokumen lain.
  resetUploadErrors(): void {
    this.imageErrors = {
      imageId: "",
      imageTax: "",
      imageBeforePacking: "",
      imageAfterPacking: ""
    };
    this.additionalDocumentError = "";
  }

  isRequired(key: SupportingDocFieldKey): boolean {
    return !!this.fieldRules?.[key]?.isRequired;
  }

  isDisabled(key: SupportingDocFieldKey): boolean {
    return (
      this.configRules?.[key]?.rule.includes(ConfigValidationForm.NON_EDITABLE) ||
      this.fieldRules?.[key]?.rule === "DISABLED"
    );
  }

  isHidden(key: SupportingDocFieldKey): boolean {
    return (
      this.configRules?.[key]?.rule.includes(ConfigValidationForm.NON_SHOW) ||
      false
    );
  }

  normalizeDutyTaxTerms(value: DutyTaxTermsBookingV4 | null) {
    if (!value) return;
    return String(value)
      .trim()
      .toUpperCase() === "DDP"
      ? "DDP"
      : "DAP";
  }

  get emailError(): string {
    if (!this.touched.email) return "";
    return parseValidationError(
      emailSchema(this.isRequired("email")),
      this.activeDoc.email
    );
  }

  get idNumberError(): string {
    if (!this.touched.idNumber) return "";
    return parseValidationError(
      idNumberSchema(this.isRequired("idNumber")),
      this.activeDoc.idNumber
    );
  }

  get taxNumberError(): string {
    if (!this.touched.taxNumber) return "";
    return parseValidationError(
      taxNumberSchema(this.isRequired("taxNumber"), this.isFtz),
      this.activeDoc.taxNumber
    );
  }

  get dutyTaxTermsOptions() {
    let options;
    if (this.isRetail) {
      options = [{ name: this.getTranslationText("dap"), value: "DAP" }];
    } else {
      options = [
        { name: this.getTranslationText("dap"), value: "DAP" },
        { name: this.getTranslationText("ddp"), value: "DDP" }
      ]
    }
    return options;
  }

  get dutyTaxTermsError(): string {
    if (this.isReverseJourney) return "";
    if (!this.isCipl || !this.touched.dutyTaxTerms) return "";
    return parseValidationError(
      dutyTaxTermsSchema(),
      this.activeDoc.dutyTaxTerms
    );
  }

  getTranslationText(translationKey = "") {
    return this.$t("bookingV4.doc.dutyTaxTerms." + translationKey);
  }

  get effectiveDutyTaxTerms() {
    return this.normalizeDutyTaxTerms(this.activeDoc.dutyTaxTerms);
  }

  get titleSection() {
    if (this.isFtz) {
      return this.$t("bookingV4.doc.titleFTZ");
    } else {
      return this.$t("bookingV4.doc.title");
    }
  }

  get isDisabledDutyTaxTerms() {
    return this.isDisabled("dutyTaxTerms") || this.isRetail || this.isEdit;
  }

  onFieldChange() {
    this.emitModelValue();
  }

  onFieldBlur(field: "email" | "idNumber" | "taxNumber") {
    this.touched[field] = true;
  }

  setDutyTaxTermsMenuOpen(isOpen: boolean) {
    if (!isOpen) {
      this.touched.dutyTaxTerms = true;
    }
    if (this.isDutyTaxTermsMenuOpen === isOpen) return;
    this.isDutyTaxTermsMenuOpen = isOpen;
  }

  onDutyTaxTermsChange(_name: string, value: DutyTaxTermsBookingV4) {
    this.activeDoc.dutyTaxTerms = value || null;
    this.emitModelValue();
  }

  onImageChange(key: SupportingDocImageFieldKey) {
    if (this.imageErrors[key]) this.validateRequiredImage(key);
    this.emitModelValue();
  }

  onImageError(key: SupportingDocImageFieldKey) {
    this.imageErrors[key] = "bookingV4.doc.upload.error";
  }

  getImageValue(key: SupportingDocImageFieldKey): unknown {
    if (key === "imageId") return this.activeDoc.imageIdUrl;
    if (key === "imageTax") return this.activeDoc.imageTaxUrl;
    if (key === "imageBeforePacking") {
      return this.activeDoc.imageBeforePackingUrl;
    }
    return this.activeDoc.imageAfterPackingUrl;
  }

  validateRequiredImage(key: SupportingDocImageFieldKey) {
    this.imageErrors[key] = imageRequiredError(
      this.isRequired(key),
      this.getImageValue(key)
    );
  }

  validateRequiredImages() {
    const keys: SupportingDocImageFieldKey[] = [
      "imageId",
      "imageTax",
      "imageBeforePacking",
      "imageAfterPacking"
    ];
    keys.forEach((key) => this.validateRequiredImage(key));
  }

  touchAllFields() {
    this.touched.email = true;
    this.touched.idNumber = true;
    this.touched.taxNumber = true;
    this.touched.dutyTaxTerms = true;
  }

  async onImageUpload(
    key: SupportingDocImageFieldKey,
    payload: UploadMultiple
  ) {
    if (!payload.file) return;
    try {
      const uploadedUrl = await this.uploadImageToHydra(payload);
      this.setImageUrl(key, uploadedUrl);
      this.imageErrors[key] = "";
    } catch (_error) {
      this.setImageUrl(key, "");
      this.imageErrors[key] = "bookingV4.doc.upload.uploadFailed";
    }
    this.emitModelValue();
  }

  async uploadImageToHydra(uploadData: UploadMultiple): Promise<string> {
    MainAppController.showLoading();
    try {
      const response = await BookingController.uploadHydraFile(
        new UploadHydraFileRequest({
          attachFile: uploadData.file,
          folderPath: "/stt"
        })
      );
      if (typeof response.data !== "string" || !response.data) {
        throw new Error("Empty supporting document URL");
      }
      return response.data;
    } finally {
      MainAppController.closeLoading();
    }
  }

  setImageUrl(key: SupportingDocImageFieldKey, uploadedUrl: string) {
    if (key === "imageId") this.activeDoc.imageIdUrl = uploadedUrl;
    if (key === "imageTax") this.activeDoc.imageTaxUrl = uploadedUrl;
    if (key === "imageBeforePacking") {
      this.activeDoc.imageBeforePackingUrl = uploadedUrl;
    }
    if (key === "imageAfterPacking") {
      this.activeDoc.imageAfterPackingUrl = uploadedUrl;
    }
  }

  triggerAddAdditionalDocument() {
    if (
      this.modelValue.additionalDocuments.length >= this.maxAdditionalDocuments
    ) {
      return;
    }
    this.additionalDocumentError = "";
    this.$refs.additionalDocumentInput?.click();
  }

  async onAdditionalDocumentSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = "";
    if (
      !file ||
      this.modelValue.additionalDocuments.length >= this.maxAdditionalDocuments
    ) {
      return;
    }

    const result = additionalDocumentFileSchema.safeParse(file);
    if (!result.success) {
      this.additionalDocumentError =
        result.error.issues[0]?.message ??
        "bookingV4.doc.upload.limitMessage";
      return;
    }

    this.additionalDocumentError = "";
    const documentId = `add-doc-${Date.now()}-${
      this.modelValue.additionalDocuments.length
    }`;
    const document = new AdditionalDocumentBookingV4({
      id: documentId,
      url: URL.createObjectURL(file),
      fileName: this.generateAdditionalDocumentFileName(file),
      fileSize: file.size
    });
    this.modelValue.additionalDocuments.push(document);
    this.emitModelValue();

    try {
      const uploadedUrl = await this.uploadImageToHydra(
        new UploadMultiple({ file })
      );
      URL.revokeObjectURL(document.url);
      document.url = uploadedUrl;
      this.emitModelValue();
    } catch (_error) {
      URL.revokeObjectURL(document.url);
      this.modelValue.additionalDocuments = this.modelValue.additionalDocuments.filter(
        (item) => item.id !== documentId
      );
      this.additionalDocumentError = "bookingV4.doc.upload.uploadFailed";
      this.emitModelValue();
    }
  }

  isPdfDocument(document: AdditionalDocumentBookingV4): boolean {
    return this.getAdditionalDocumentFileName(document, 0)
      .toLowerCase()
      .endsWith(".pdf");
  }

  generateAdditionalDocumentFileName(file: File): string {
    if (file.name.trim()) return file.name;

    const extension = file.type.split("/").pop() || "file";
    return `document-${Date.now()}.${extension}`;
  }

  getAdditionalDocumentFileName(
    document: AdditionalDocumentBookingV4,
    index: number
  ): string {
    if (document.fileName?.trim()) return document.fileName;

    const urlFileName =
      document.url
        .split(/[?#]/)[0]
        .split("/")
        .pop() || "";
    if (urlFileName.includes(".")) {
      try {
        return decodeURIComponent(urlFileName);
      } catch {
        return urlFileName;
      }
    }

    return `document-${index + 1}`;
  }

  getAdditionalDocumentFileSize(document: AdditionalDocumentBookingV4): string {
    const bytes = document.fileSize;
    if (!bytes) return "-";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Number((bytes / 1024).toFixed(1))} KB`;
    return `${Number((bytes / (1024 * 1024)).toFixed(2))} MB`;
  }

  onRemoveAdditionalDocument(id: string) {
    const removed = this.modelValue.additionalDocuments.find(
      (doc) => doc.id === id
    );
    if (removed?.url.startsWith("blob:")) URL.revokeObjectURL(removed.url);

    this.modelValue.additionalDocuments = this.modelValue.additionalDocuments.filter(
      (doc) => doc.id !== id
    );
    this.emitModelValue();
  }

  beforeUnmount() {
    this.modelValue.additionalDocuments.forEach((doc) => {
      if (doc.url.startsWith("blob:")) URL.revokeObjectURL(doc.url);
    });
  }

  emitModelValue() {
    this.$emit("update:modelValue", this.modelValue);
  }

  emitValidationChange() {
    this.$emit("validationChange", this.isSectionValid);
    this.errors.supportingDoc = "";
  }

  get isSectionValid(): boolean {
    const doc = this.activeDoc;
    const fieldsValid = [
      !parseValidationError(emailSchema(this.isRequired("email")), doc.email),
      !parseValidationError(
        idNumberSchema(this.isRequired("idNumber")),
        doc.idNumber
      ),
      !parseValidationError(
        taxNumberSchema(this.isRequired("taxNumber"), this.isFtz),
        doc.taxNumber
      ),
      !imageRequiredError(this.isRequired("imageId"), doc.imageIdUrl),
      !imageRequiredError(this.isRequired("imageTax"), doc.imageTaxUrl),
      !imageRequiredError(
        this.isRequired("imageBeforePacking"),
        doc.imageBeforePackingUrl
      ),
      !imageRequiredError(
        this.isRequired("imageAfterPacking"),
        doc.imageAfterPackingUrl
      )
    ].every(Boolean);

    const dutyTaxTermsValid =
      this.isReverseJourney || !this.isCipl || !!doc.dutyTaxTerms;

    return fieldsValid && dutyTaxTermsValid;
  }

  formatNumberOnly(value: string, ableInputZero = true) {
    if (!this.isFtz) return value;
    const noDecimal = !ableInputZero ? formatInputNumberOnlyV2(String(value)) : formatNumberOnlyWithZero(String(value), ableInputZero);
    return noDecimal;
  }
}
